// ONE SEARCH FOR EVERY DROPDOWN IN THE APP.
//
// Owner, 2026-10-22: "all drop down search in edit issue page in linked to
// column/panel, when we type value it doesn't filter select result correctly
// ... same issue exists in all dropdown search on all tabs ... why not fix
// the component of dropdown and use it everywhere, and just change the data
// it is fed to it".
//
// WHAT WAS WRONG. Every search in this app was ONE SUBSTRING:
//
//     String(option.search || option.label || "").toLowerCase().includes(q)
//
// so the typed text had to appear in the option, contiguous and in that
// order. Two words the reader can see on screen do not match when anything
// sits between them: a leaf activity reads "Area E › Level 2 › Busway" and
// typing "area busway" - both words, in the order shown - returns nothing.
// (leafActivityOptions' own header claimed "Area E busway finds it from
// either end"; a substring test cannot do that, and it never did.)
//
// THE RULE HERE: every WORD typed must appear somewhere in the option, in
// any order. That is strictly more forgiving than a substring - a single
// word still matches exactly what it used to - so adopting it cannot make a
// dropdown find less than it found before.
//
// WHERE THE WORDS COME FROM. `search` if the option has one, else `text`,
// else `title`, else `label` ONLY WHEN IT IS A STRING. A label is very often
// JSX in this codebase (PartnerWithTrades, the two-line activity row), and
// String(<element/>) is "[object Object]" - which matches the letter "o" and
// nothing a person would type. Options carrying JSX labels should give a
// `search`; where they do not, `value` is the last resort so the list filters
// on something real rather than on everything.
import React from "react";
import { Select } from "antd";

/** Accents folded, punctuation to spaces: "Lévy-Strauss" ~ "levy strauss". */
const fold = (s) => String(s == null ? "" : s)
  .normalize("NFD").replace(/[̀-ͯ]/g, "")
  .toLowerCase()
  .replace(/[^\p{L}\p{N}]+/gu, " ")
  .trim();

/**
 * The words a person could type to find this option. Exported so a page can
 * check what its own options are searchable by, and so the tests can read it.
 */
export function searchText(option) {
  if (!option) return "";
  const parts = [];
  for (const key of ["search", "text", "title"]) {
    if (typeof option[key] === "string" && option[key]) parts.push(option[key]);
  }
  // A JSX label is not text; only a string one is.
  if (typeof option.label === "string" && option.label) parts.push(option.label);
  if (typeof option.short === "string" && option.short) parts.push(option.short);
  // A Select written with <Option>text</Option> children rather than an
  // options array (4 of them here) puts its words in `children`.
  if (typeof option.children === "string" && option.children) parts.push(option.children);
  if (!parts.length && typeof option.value === "string") parts.push(option.value);
  return parts.join(" ");
}

/**
 * antd `filterOption`: every typed word must appear in the option, in any
 * order. A GROUP row ({ options: [...] }) is never matched on its own -
 * antd shows a group whenever one of its children matches, and matching the
 * heading itself would drag in children that match nothing.
 */
/**
 * The same rule against plain text, for anything that is not a dropdown -
 * a table search, a list filter. Exported so the app has ONE search
 * behaviour: a person who learns that typing two words works in a dropdown
 * should not find it works differently in a table.
 */
export function matchText(input, text) {
  const q = fold(input);
  if (!q) return true;
  const hay = fold(text);
  if (!hay) return false;
  const words = hay.split(" ");
  return q.split(" ").every((w) => (/^\d+$/.test(w) ? words.includes(w) : hay.includes(w)));
}

export function matchOption(input, option) {
  const q = fold(input);
  if (!q) return true;
  if (option && Array.isArray(option.options)) return false;
  const hay = fold(searchText(option));
  if (!hay) return false;
  // A NUMBER MUST BE A WHOLE WORD. Options carry text nobody sees - a due
  // date, a package code, an id - and a bare digit is inside most of them:
  // typing "Level 2" matched a Level 1 row because its due date was
  // 2026-10-01 and "2" is in "2026". Letters do not have this problem (a
  // typed word is almost never a stray run of letters) and matching them
  // inside a word is how people search, so only digits are tightened.
  //
  // The cost, said plainly: typing "202" no longer finds "2026". Typing the
  // whole number does, and so does "10" for the month - a part-typed number
  // is a far rarer way to search than a level or a revision is, and silently
  // matching the wrong row is the worse failure of the two.
  const words = hay.split(" ");
  // Every word, anywhere: "area busway" finds "Area E › Level 2 › Busway".
  return q.split(" ").every((w) => (/^\d+$/.test(w) ? words.includes(w) : hay.includes(w)));
}

/**
 * The dropdown to use everywhere. An ordinary antd Select with the shared
 * search already on it - pass `options` and nothing else changes. Every
 * Select prop passes straight through, so a caller that needs its own
 * `filterOption` (or no search at all, with `showSearch={false}`) can still
 * say so and wins.
 */
export const SearchSelect = React.forwardRef(function SearchSelect(props, ref) {
  const { showSearch = true, filterOption, ...rest } = props;
  return (
    <Select
      ref={ref}
      showSearch={showSearch}
      filterOption={filterOption === undefined ? matchOption : filterOption}
      {...rest}
    />
  );
});

export default SearchSelect;
